import { getDabUrl } from './api';

// Log entry interface
export interface LogEntry {
  time: string;
  actionTaken: string;
  byWhom: string;
  decisionOutcome: string;
  nextSteps: string;
}

// Server-side interface (matches database schema)
export interface CrisisLogServer {
  id?: number;
  owner_user_id?: string;
  owner_email?: string;
  owner_name?: string | null;
  incident_crisis_title: string;
  date_time_crisis_declared: string; // ISO datetime
  duty_officer_on_call: string;
  crisis_management_team_lead: string;
  log_entries?: string | null; // JSON string
  closure_date_time?: string | null;
  after_action_review_completed: boolean;
  status: 'active' | 'closed';
  created_at?: string;
  last_modified?: string;
  travel_form_id?: number | null;
}

// Client-side interface (camelCase for React)
export interface CrisisLogClient {
  id?: number;
  ownerUserId?: string;
  ownerEmail?: string;
  ownerName?: string;
  incidentCrisisTitle: string;
  dateTimeCrisisDeclared: string;
  dutyOfficerOnCall: string;
  crisisManagementTeamLead: string;
  logEntries: LogEntry[];
  closureDateTime: string;
  afterActionReviewCompleted: boolean;
  status: 'active' | 'closed';
  createdAt?: string;
  lastModified?: string;
  travelFormId?: number | null;
}

/**
 * Convert server format to client format
 */
export function fromServerCrisisLog(server: CrisisLogServer): CrisisLogClient {
  let logEntries: LogEntry[] = [];
  if (server.log_entries) {
    try {
      logEntries = JSON.parse(server.log_entries);
    } catch (e) {
      console.error('Failed to parse log entries:', e);
    }
  }

  return {
    id: server.id,
    ownerUserId: server.owner_user_id,
    ownerEmail: server.owner_email,
    ownerName: server.owner_name || undefined,
    incidentCrisisTitle: server.incident_crisis_title,
    dateTimeCrisisDeclared: server.date_time_crisis_declared,
    dutyOfficerOnCall: server.duty_officer_on_call,
    crisisManagementTeamLead: server.crisis_management_team_lead,
    logEntries: logEntries,
    closureDateTime: server.closure_date_time || '',
    afterActionReviewCompleted: server.after_action_review_completed,
    status: server.status,
    createdAt: server.created_at,
    lastModified: server.last_modified,
    travelFormId: server.travel_form_id,
  };
}

/**
 * Convert client format to server format
 */
export function toServerCrisisLog(
  client: CrisisLogClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): CrisisLogServer {
  return {
    id: client.id,
    owner_user_id: ownerUserId,
    owner_email: ownerEmail,
    owner_name: ownerName,
    incident_crisis_title: client.incidentCrisisTitle,
    date_time_crisis_declared: client.dateTimeCrisisDeclared,
    duty_officer_on_call: client.dutyOfficerOnCall,
    crisis_management_team_lead: client.crisisManagementTeamLead,
    log_entries: JSON.stringify(client.logEntries),
    closure_date_time: client.closureDateTime || null,
    after_action_review_completed: client.afterActionReviewCompleted,
    status: client.status,
    travel_form_id: client.travelFormId || null,
  };
}

/**
 * Fetch all crisis logs for the current user
 */
export async function fetchCrisisLogs(): Promise<CrisisLogClient[]> {
  const response = await fetch(getDabUrl('CrisisLogs'), {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch crisis logs: ${response.statusText}`);
  }

  const data = await response.json();
  const logs = data.value || [];
  return logs.map(fromServerCrisisLog);
}

/**
 * Fetch a single crisis log by ID
 */
export async function fetchCrisisLogById(id: number): Promise<CrisisLogClient | null> {
  const response = await fetch(getDabUrl(`CrisisLogs/id/${id}`), {
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`Failed to fetch crisis log: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerCrisisLog(data);
}

/**
 * Create a new crisis log
 */
export async function createCrisisLog(
  log: CrisisLogClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<CrisisLogClient> {
  const serverLog = toServerCrisisLog(log, ownerUserId, ownerEmail, ownerName);

  const response = await fetch(getDabUrl('CrisisLogs'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(serverLog),
  });

  if (!response.ok) {
    throw new Error(`Failed to create crisis log: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerCrisisLog(data);
}

/**
 * Update an existing crisis log
 */
export async function updateCrisisLog(
  log: CrisisLogClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<CrisisLogClient> {
  if (!log.id) {
    throw new Error('Cannot update crisis log without an ID');
  }

  const serverLog = toServerCrisisLog(log, ownerUserId, ownerEmail, ownerName);

  const response = await fetch(getDabUrl(`CrisisLogs/id/${log.id}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(serverLog),
  });

  if (!response.ok) {
    throw new Error(`Failed to update crisis log: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerCrisisLog(data);
}

/**
 * Delete a crisis log
 */
export async function deleteCrisisLog(id: number): Promise<void> {
  const response = await fetch(getDabUrl(`CrisisLogs/id/${id}`), {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete crisis log: ${response.statusText}`);
  }
}

/**
 * Save crisis log (create or update)
 */
export async function saveCrisisLog(
  log: CrisisLogClient,
  ownerUserId: string,
  ownerEmail: string,
  ownerName: string
): Promise<CrisisLogClient> {
  if (log.id) {
    return await updateCrisisLog(log, ownerUserId, ownerEmail, ownerName);
  } else {
    return await createCrisisLog(log, ownerUserId, ownerEmail, ownerName);
  }
}
